import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';


import { Box } from 'ui/box';

import Tabs from 'components/partials/tabs/tabs';
import toast from 'components/partials/toast/toast';

import GoalHeader from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-header/goal-header';
import GoalForm from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/goal-form';

import History from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-tabs/goal-history-tab';
import CriticalElements from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-tabs/goal-critical-elements-tab';
import {
  GoalFormProvider,
  useGoalForm,
} from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/context/context';

import { useCurrentUser } from 'hooks/api/use-current-user';

import useGoalMonitoringVisibility from 'hooks/use-goal-monitoring-visibility';
import useGoalCriticalElements from 'hooks/use-goal-critical-elements';

import { getCurrentFiscalYear } from 'components/partials/insight-modal/shared/halpers';

import {
  getPathToGoal,
  getUrlParams,
  prepareGoalPayload,
  getPathSegmentsFromValues,
} from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-manager.helper';
import { getActionMenuItems } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal.helpers';

import {
  createClientGoal,
  deleteClientGoal,
  updateClientGoal,
} from 'api/generated/client-goals/client-goals';
import type { ClientGoalDto } from 'api/generated/schemas';

import type { Goal as GoalType } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface TabItem {
  label: string;
  value: string;
  isDisabled: boolean;
}

const getTabItems = ({ isDisabled }: { isDisabled: boolean }): TabItem[] => [
  { label: 'Details', value: 'details', isDisabled: false },
  { label: 'Critical Elements', value: 'criticalElements', isDisabled },
  { label: 'Version History', value: 'history', isDisabled },
];

interface GoalProps {
  data: GoalType | undefined;
  goalsLength: number;
  isEditMode: boolean;
  isCreation: boolean;
  setIsEditMode: (value: boolean) => void;
  getGoals: () => Promise<void>;
  selectedGoalId: string | number;
  setLoading: (value: boolean) => void;
  loading: boolean;
}

const Goal = ({
  data,
  goalsLength,
  isEditMode,
  isCreation,
  setIsEditMode,
  getGoals,
  selectedGoalId,
  setLoading,
  loading,
}: GoalProps): React.JSX.Element => (
  <GoalFormProvider
    defaultValues={{
      value: '',
      relativeToLastYear: 'Increase',
      details: [],
      name: '',
      description: '',
    }}
    loading={loading}
  >
    <GoalInner
      data={data}
      goalsLength={goalsLength}
      isEditMode={isEditMode}
      isCreation={isCreation}
      setIsEditMode={setIsEditMode}
      getGoals={getGoals}
      selectedGoalId={selectedGoalId}
      setLoading={setLoading}
    />
  </GoalFormProvider>
);

type GoalInnerProps = Omit<GoalProps, 'loading'>;

interface CurrentUserShape {
  data?: { oktaUserId?: string };
}

const GoalInner = ({
  data,
  goalsLength,
  isEditMode,
  setIsEditMode,
  getGoals,
  selectedGoalId,
  isCreation,
  setLoading,
}: GoalInnerProps): React.JSX.Element => {
  const [goal, setGoal] = useState<
    GoalType | Record<string, never> | undefined
  >(data);
  const [activeTab, setActiveTab] = useState<string>('details');

  const params = useParams();
  const navigate = useNavigate();

  const { type, category, enteringClassYear, clientId } = getUrlParams(params);
  const { getValues, handleSubmit, reset } = useGoalForm();

  const user = useCurrentUser() as CurrentUserShape;
  const userId = user?.data?.oktaUserId;

  const isCurrentFiscalYear =
    Number(enteringClassYear) === getCurrentFiscalYear();

  const { criticalElements } = useGoalCriticalElements(
    (goal as GoalType | undefined)?.id,
    userId
  );

  const { shouldShowMonitoring } = useGoalMonitoringVisibility(
    criticalElements,
    isCurrentFiscalYear,
    clientId
  );

  useEffect(() => {
    if (isCreation) {
      setGoal({});
      reset();
    }
  }, [isCreation, reset]);

  useEffect(() => {
    setGoal(data);
  }, [data]);

  useEffect(() => {
    setActiveTab('details');
  }, [selectedGoalId]);

  const handleMonitoringClick = (): void => {
    const goalId = (goal as GoalType | undefined)?.id;

    if (!goalId) return;

    const { goalType, goalCategory } = getPathSegmentsFromValues({
      type,
      category,
    });

    navigate(
      `/admin/clients/${clientId}/goals/${enteringClassYear}/${goalType}/${goalCategory}/${goalId}/monitoring`
    );
  };

  const onCancel = useCallback(
    (isNew: boolean): void => {
      if (isEditMode) {
        setIsEditMode(false);

        return;
      }

      if (isNew && !goalsLength) {
        navigate(`/admin/clients/${clientId}/goals/${enteringClassYear}`);
      } else {
        const goalPath = getPathToGoal(window.location.pathname);

        navigate(goalPath);
      }
    },
    [clientId, goalsLength, navigate, isEditMode, setIsEditMode]
  );

  const onSubmit = useCallback(async (): Promise<void> => {
    if (!userId) return;

    const formData = getValues();
    const goalId = (goal as GoalType | undefined)?.id;

    const rawPayload = prepareGoalPayload({
      userId,
      clientId,
      data: { ...(data ?? {}), type, category, enteringClassYear } as never,
      formData: formData as never,
    });
    const goalDto = rawPayload.goalData as ClientGoalDto;

    try {
      setLoading(true);
      if (goalId) {
        await updateClientGoal(
          userId,
          Number(clientId),
          Number(goalId),
          goalDto
        );

        if (isEditMode) {
          setIsEditMode(false);
        }
        await getGoals();
      } else {
        await createClientGoal(userId, Number(clientId), goalDto);
        const pathToGoal = getPathToGoal(window.location.pathname);

        navigate(pathToGoal);
      }
      toast.success({
        title: `The goal was successfully ${goalId ? 'updated' : 'created'}`,
      });
    } catch (e) {
      toast.error({ title: 'Something went wrong' });
      console.error(
        `
        Error when ${goalId ? 'editing' : 'creating'} a goal`,
        e
      );
      setLoading(false);
    }
  }, [
    category,
    clientId,
    data,
    enteringClassYear,
    getGoals,
    getValues,
    goal,
    navigate,
    isEditMode,
    setIsEditMode,
    type,
    userId,
  ]);

  const handleFormSubmit = handleSubmit(onSubmit);

  const handleDelete = useCallback(
    async (id: string | number): Promise<void> => {
      if (!userId) return;

      setLoading(true);
      try {
        await deleteClientGoal(userId, Number(clientId), Number(id));
        const goalPath = getPathToGoal(window.location.pathname);

        navigate(goalPath);
        toast.success({
          title: `The goal was successfully removed`,
        });
      } catch (e) {
        toast.error({ title: 'Something went wrong' });

        console.error('Error when deleting a goal', e);
        setLoading(false);
      }
    },
    [clientId, navigate, userId]
  );

  const renderTab = (
    renderData: GoalType & {
      type?: string;
      category?: string;
      enteringClassYear?: number;
    }
  ): React.JSX.Element | null => {
    const params = {
      type: renderData.type,
      category: renderData.category,
      isCreation,
      userId,
      enteringClassYear,
      ...(renderData.id ? { isEditing: isEditMode, data: renderData } : {}),
    };

    switch (activeTab) {
      case 'details': {
        const goalFormProps = params as unknown as React.ComponentProps<
          typeof GoalForm
        >;

        return <GoalForm {...goalFormProps} />;
      }

      case 'criticalElements':
        return (
          <CriticalElements
            data={renderData}
            clientId={clientId}
            userId={userId}
          />
        );

      case 'history':
        return (
          <History
            data={{
              id: renderData.id ?? 0,
              category: renderData.category ?? '',
            }}
            clientId={clientId}
            userId={userId}
          />
        );
      default:
        return null;
    }
  };

  const goalId = (goal as GoalType | undefined)?.id;

  return (
    <Box sx={{ width: '100%', pt: 2, pl: 2 }}>
      <Tabs
        items={getTabItems({ isDisabled: !goalId || isEditMode })}
        activeTab={activeTab}
        onSelect={setActiveTab}
        customClass="mb-3"
        data-testid="goal-tabs"
      />
      <GoalHeader
        actionMenuItems={getActionMenuItems({
          handleDelete: (id) => {
            void handleDelete(id);
          },
          goalId,
        })}
        isEditMode={isEditMode}
        onEditClick={() => setIsEditMode(true)}
        sx={{ mb: 2.5 }}
        isNew={!goalId}
        onCancel={onCancel}
        onSubmit={handleFormSubmit}
        activeTab={activeTab}
        onMonitoringClick={handleMonitoringClick}
        shouldShowMonitoring={shouldShowMonitoring}
      />
      {renderTab({
        ...(goal as GoalType),
        type,
        category,
        enteringClassYear,
      } as never)}
    </Box>
  );
};

export default React.memo(Goal);
